import { Accordion, AccordionButton, AccordionIcon, AccordionItem, AccordionPanel, Alert, AlertDescription, AlertIcon, AlertTitle, Badge, Box, Button, Divider, Flex, FormControl, FormLabel, HStack, Input, Stack, Text, useColorModeValue } from '@chakra-ui/react';
import React, { useEffect } from 'react';
import { addCompanyRegistrationRequest, deleteCompanyRegistrationRequest, getCompanyRegistrationRequest, updateCompanyRegistrationRequest } from '../../store/actions';
import { companyRequestRegistrationFormValidation, filterByStatus } from '../../utils';
import { useAppDispatch, useAppSelector } from '../../hooks';

import { AppDispatch } from '../../store';
import { ToastContainer } from 'react-toastify';
import { requestCompanyRegistration } from '../../types';
import { useFormik } from 'formik';
import { usePaystackPayment } from 'react-paystack';

export const CompanyRegister = () => {

    const { user } = useAppSelector(state => state.userReducer);

    const { company_registrations_request, is_updating_company_registration_request, is_adding_company_registration_request, is_deleting_company_registration_request } = useAppSelector(state => state.companyRegistrationRequest);

    const dispatch: AppDispatch = useAppDispatch();

    const companyForm = useFormik({
        initialValues: {
            phone: '',
            email: '',
            company_name: '',
            amount: 500
        },
        validationSchema: companyRequestRegistrationFormValidation,
        onSubmit: (values: requestCompanyRegistration) => {
            dispatch(addCompanyRegistrationRequest(values)).then(() => { dispatch(getCompanyRegistrationRequest()); resetForm(); });
            // alert(JSON.stringify(values, null, 2));
        }
    });

    const config: any = {
        firstname: String(user?.first_name),
        lastname: String(user?.last_name),
        phone: String(user?.phone),
        email: String(user?.email),
        amount: Number(`${500}00`),
        publicKey: process.env.REACT_APP_PAYSTACK_PUBLICK_KEY,
        currency: 'ZAR',
    };

    const initializePayment = usePaystackPayment(config);

    const _onClose = () => {
        console.log('closed');
    };

    const handlePayment = async (values: requestCompanyRegistration) => {
        initializePayment((reference: void) => {
            const data: any = reference;
            const _values = { ...values, payment_status: 'paid', payment_reference: String(data?.reference) };
            dispatch(updateCompanyRegistrationRequest(_values)).then(() => dispatch(getCompanyRegistrationRequest()));
        }, _onClose);
    };

    const handleRequestCancel = (request: requestCompanyRegistration) => {
        const values = { ...request, status: 'cancelled' };
        dispatch(updateCompanyRegistrationRequest(values)).then(() => dispatch(getCompanyRegistrationRequest()));
    };

    const handleRequestPending = (request: requestCompanyRegistration) => {
        const values = { ...request, status: 'pending' };
        dispatch(updateCompanyRegistrationRequest(values)).then(() => dispatch(getCompanyRegistrationRequest()));
    };

    const handleRequestDelete = (id: string) => {
        dispatch(deleteCompanyRegistrationRequest(id)).then(() => dispatch(getCompanyRegistrationRequest()));
    };

    const resetForm = () => companyForm.resetForm();

    useEffect(() => {
        dispatch(getCompanyRegistrationRequest());
    }, []);

    return (
        <Flex
            minH={'-webkit-fill-available'}
            align={'center'}
            justify={'center'}
            // bg={useColorModeValue('gray.50', 'gray.800')}
            py={10}
        >
            <Stack>
                {
                    company_registrations_request?.filter((request: requestCompanyRegistration) => request.status === 'pending')?.length &&
                    <Alert status='success' variant='subtle' shadow={'sm'} mb={2} borderRadius={6}>
                        <AlertIcon />
                        <Box>
                            <AlertTitle>Success!</AlertTitle>
                            <AlertDescription>
                                Your application has been received. We will review your application
                                and respond within the next few hours.
                            </AlertDescription>
                        </Box>
                    </Alert>
                }
                {
                    company_registrations_request?.filter((request: requestCompanyRegistration) => request.status === 'approved' && request?.payment_status === 'paid')?.length &&
                    <Alert status='success' variant='subtle' shadow={'sm'} mb={2} borderRadius={6}>
                        <AlertIcon />
                        <Box>
                            <AlertTitle>Payment Success!</AlertTitle>
                            <AlertDescription>
                                Your payment has been received. We&apos;re busy registering your company and it will be active in the next 2-3 days.
                            </AlertDescription>
                        </Box>
                    </Alert>
                }
                {
                    company_registrations_request?.filter((request: requestCompanyRegistration) => request.status === 'cancelled')?.length &&
                    <Alert status='warning' shadow={'sm'} mb={3} borderRadius={6}>
                        <AlertIcon />
                        Cancellation received, if you change your mind and you want to register your business, please contact us on 0761958601.
                    </Alert>
                }
                {
                    company_registrations_request.length &&
                    <Accordion defaultIndex={[0]} allowMultiple bg={'#FFFFFF'} borderRadius={6}>
                        <AccordionItem>
                            <h2>
                                <AccordionButton _hover={{ bg: '#fff8e1' }}>
                                    <Box flex='1' textAlign='left'>
                                        <Text fontWeight={'medium'}>Company registartion requests: Total <Badge>{company_registrations_request?.length}</Badge></Text>
                                        <HStack mt={2}>
                                            {filterByStatus(company_registrations_request, 'pending')?.length && <Badge colorScheme='green'>pending {filterByStatus(company_registrations_request, 'pending')?.length}</Badge>}
                                            {filterByStatus(company_registrations_request, 'cancelled')?.length && <Badge colorScheme='red'>cancelled {filterByStatus(company_registrations_request, 'cancelled')?.length}</Badge>}
                                            {filterByStatus(company_registrations_request, 'approved')?.length && <Badge colorScheme='green'>approved {filterByStatus(company_registrations_request, 'approved')?.length}</Badge>}
                                            {filterByStatus(company_registrations_request, 'completed')?.length && <Badge colorScheme='green'>completed {filterByStatus(company_registrations_request, 'completed')?.length}</Badge>}
                                        </HStack>
                                    </Box>
                                    <AccordionIcon />
                                </AccordionButton>
                            </h2>
                            <AccordionPanel pb={4} bg={'#f5f5f5'}>
                                {
                                    company_registrations_request?.length &&
                                    <>
                                        {
                                            company_registrations_request.map((request: requestCompanyRegistration, index: number) => (
                                                <HStack key={index} bg={'#FFFFFF'} px={3} py={2} mt={2} justifyContent={'space-between'} borderRadius={4} shadow={'sm'}>
                                                    <Stack>
                                                        <Text textTransform={'capitalize'} fontWeight={'medium'} fontSize={16}>{request?.company_name} <Badge colorScheme={request?.status === 'cancelled' ? 'red' : 'green'}>{request?.status}</Badge></Text>
                                                        <HStack>
                                                            <Text fontWeight={'light'}>{request?.request_id}</Text>
                                                            <Badge colorScheme={request?.payment_status === 'paid' ? 'green' : 'gray'}>{request?.payment_status === 'paid' ? 'Paid' : 'Unpaid'}</Badge>
                                                        </HStack>
                                                    </Stack>
                                                    <HStack>
                                                        {request?.status === 'approved' && request?.payment_status === 'unpaid' && <Button colorScheme='green' size={'sm'} onClick={() => handlePayment(request)} >Pay {request?.amount}</Button>}
                                                        {request?.status === 'cancelled' && <Button colorScheme='yellow' size={'sm'} onClick={() => handleRequestPending(request)} >Re-Send Request</Button>}
                                                        {request?.status === 'cancelled' && <Button loadingText='Deleting' colorScheme='red' size={'sm'} onClick={() => handleRequestDelete(`${request?.request_id}`)} disabled={is_updating_company_registration_request || is_deleting_company_registration_request} isLoading={is_updating_company_registration_request || is_deleting_company_registration_request} >Delete</Button>}
                                                        {(request?.status === 'pending' || request?.status === 'approved') && <Button loadingText={'cancelling'} colorScheme='red' size={'sm'} onClick={() => handleRequestCancel(request)} isLoading={is_updating_company_registration_request || is_deleting_company_registration_request} disabled={is_updating_company_registration_request || is_deleting_company_registration_request || request?.payment_status === 'paid'}>{'Cancel'}</Button>}
                                                    </HStack>
                                                </HStack>
                                            ))
                                        }
                                    </>
                                }
                            </AccordionPanel>
                        </AccordionItem>
                    </Accordion>
                }
                <Box
                    rounded={'lg'}
                    bg={useColorModeValue('white', 'gray.700')}
                    boxShadow={'lg'}
                    p={8}>
                    <Stack spacing={4}>
                        <HStack align={'center'} alignSelf={'center'}>
                            <Stack>
                                <Text display={'flex'} fontSize={'lg'} fontWeight='medium' textAlign={'center'}>
                                    Request company registration.
                                </Text>
                            </Stack>
                        </HStack>
                        <Divider />
                        <Stack mb={3}>
                            <Text textAlign={'center'} fontSize={'lg'} color={'gray.600'}>
                                Register your business with our partners <a href='https://www.google.com' target={'_blank'} rel={'noreferrer'}>company name</a>, for R500✌️
                            </Text>
                        </Stack>
                        <form onSubmit={companyForm.handleSubmit} style={{ justifyContent: 'center', display: 'grid' }}>
                            <HStack>
                                <Box>
                                    <FormControl id="phone" isRequired mb={companyForm.errors.phone ? 0 : 5}>
                                        <FormLabel>Phone number</FormLabel>
                                        <Input name='phone' type="text" value={companyForm.values.phone} onChange={companyForm.handleChange} />
                                        {companyForm.errors.phone && <Text fontSize='sm' color='red.400'>{companyForm.errors.phone}</Text>}
                                    </FormControl>
                                </Box>
                                <Box>
                                    <FormControl id="email" isRequired mb={companyForm.errors.email ? 0 : 5}>
                                        <FormLabel>Email</FormLabel>
                                        <Input name='email' type="email" value={companyForm.values.email} onChange={companyForm.handleChange} />
                                        {companyForm.errors.email && <Text fontSize='sm' color='red.400'>{companyForm.errors.email}</Text>}
                                    </FormControl>
                                </Box>
                            </HStack>
                            <HStack>
                                <Box>
                                    <FormControl id="company_name" isRequired mb={companyForm.errors.company_name ? 0 : 5}>
                                        <FormLabel>Company name</FormLabel>
                                        <Input name='company_name' type="text" value={companyForm.values.company_name} onChange={companyForm.handleChange} />
                                        {companyForm.errors.company_name && <Text fontSize='sm' color='red.400'>{companyForm.errors.company_name}</Text>}
                                    </FormControl>
                                </Box>
                            </HStack>
                            <Stack spacing={10} pt={2}>
                                <Button
                                    type='submit'
                                    isLoading={is_adding_company_registration_request}
                                    disabled={!companyForm.isValid || is_adding_company_registration_request}
                                    loadingText="Submitting"
                                    size="lg"
                                    colorScheme={'yellow'}
                                    >
                                    Submit request
                                </Button>
                            </Stack>
                        </form>
                    </Stack>
                </Box>
            </Stack>
            <ToastContainer />
        </Flex>
    );
};